import type { Direction } from "../contract/types";
import { useBackend } from "../app/BackendProvider";
import { formatDuration } from "../app/format";
import { useNetworkId } from "../config/ConfigProvider";
import { usePolledResource } from "./hooks";

/**
 * Bandeau d'indicateurs (F1.4) — purement informationnel (apport A9 retenu).
 * Tout est calculé côté serveur (`/headways`) ; l'écran ne fait qu'afficher.
 */
export function IndicatorsBar({ lineId, direction }: { lineId: string; direction: Direction }) {
  const backend = useBackend();
  const networkId = useNetworkId();
  const { data } = usePolledResource(
    (signal) => backend.api.getHeadways(networkId, { lineId, direction }, signal),
    [backend, networkId, lineId, direction],
    15_000,
  );

  const bunching = data?.bunching.length ?? 0;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        flexWrap: "nowrap",
        gap: 0,
        borderTop: "1px solid var(--sentinelle-rule)",
        borderBottom: "1px solid var(--sentinelle-rule)",
        background: "var(--sentinelle-paper)",
      }}
    >
      <Indicator label="Intervalle moyen" value={data ? formatDuration(data.meanHeadwaySeconds) : "—"} />
      <Indicator
        label="Écart max"
        value={data ? formatDuration(data.maxGapSeconds) : "—"}
        tone={data && data.maxGapSeconds >= 300 ? "signal" : "ink"}
      />
      <Indicator label="Courses en service" value={data ? `${data.inService} / ${data.scheduled}` : "—"} />
      <Indicator
        label="Bunching détecté"
        value={bunching === 0 ? "aucun" : `${bunching} paire${bunching > 1 ? "s" : ""}`}
        tone={bunching > 0 ? "signal" : "ink"}
        last
      />
    </div>
  );
}

function Indicator({
  label,
  value,
  tone = "ink",
  last,
}: {
  label: string;
  value: string;
  tone?: "ink" | "signal";
  last?: boolean;
}) {
  return (
    <div
      style={{
        padding: "12px 16px",
        borderRight: last ? undefined : "1px solid var(--sentinelle-rule)",
        flex: "0 0 auto",
      }}
    >
      <div className="ovl">{label}</div>
      <div
        className="mono"
        style={{
          fontSize: "1.35rem",
          fontWeight: 700,
          lineHeight: 1.1,
          marginTop: 5,
          color: tone === "signal" ? "var(--sentinelle-signal)" : "var(--sentinelle-ink)",
        }}
      >
        {value}
      </div>
    </div>
  );
}
